import { AvailableLanguages } from '@repo/toolkit/language-types'
import { StringFormatter } from '@repo/toolkit/string'
import { Column } from '@repo/ui/data-table'
import { TFunction } from 'i18next'

import { useNativeCountryName } from '../../../lib/hooks/use-native-country-name'
import { TeachingHall } from '../../../lib/types/models/ol/teaching-hall/teaching-hall'
import { TeachingHallInput } from '../../../lib/types/models/ol/teaching-hall/teaching-hall-input'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

export const mapTeachingHallToInput = (teachingHall: TeachingHall): TeachingHallInput => ({
  name: teachingHall.name,
  address1: teachingHall.address1,
  address2: teachingHall.address2,
  city: teachingHall.city,
  contactEmail: teachingHall.contactEmail,
  contactFirstName: teachingHall.contactFirstName,
  contactLastName: teachingHall.contactLastName,
  contactPhone: teachingHall.contactPhone,
  country: teachingHall.country,
  numberOfClassrooms: teachingHall.numberOfClassrooms,
  postalCode: teachingHall.postalCode,
  files: teachingHall.files
})

const TeachingHallAddress = ({ teachingHall }: { teachingHall: TeachingHall }) => {
  const nativeCountryName = useNativeCountryName()

  const fullAddress = [
    teachingHall.address1,
    teachingHall.address2,
    teachingHall.city,
    teachingHall.postalCode,
    nativeCountryName(teachingHall.country)
  ]
    .filter(Boolean)
    .join(', ')

  return <div className="max-w-[200px] whitespace-normal break-words">{fullAddress}</div>
}

export const teachingHallColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  enableSorting: boolean = true
): Column<TeachingHall>[] => [
  {
    accessorKey: 'name',
    header: t('teaching.hall.table.name'),
    enableSorting: enableSorting
  },
  {
    accessorKey: 'address1',
    header: t('teaching.hall.table.address'),
    enableSorting: enableSorting,
    cell: ({ row }) => <TeachingHallAddress teachingHall={row.original} />
  },
  {
    accessorKey: 'numberOfClassrooms',
    header: t('teaching.hall.table.number.of.classrooms'),
    enableSorting: enableSorting
  },
  {
    accessorKey: 'contactLastName',
    header: t('teaching.hall.table.contact'),
    enableSorting: enableSorting,
    cell: ({ row }) =>
      row.original.contactLastName.toUpperCase() +
      ' ' +
      StringFormatter.capitalize(row.original.contactFirstName)
  },
  {
    accessorKey: 'contactPhone',
    header: t('teaching.hall.table.phoneNumber'),
    cell: ({ row }) => formatPhoneNumber(row.original.contactPhone)
  }
]

export const getTeachingHallColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  enableSorting: boolean = true
): Column<TeachingHall>[] => {
  return teachingHallColumns(t, enableSorting)
}
